//-------------------------------------------------------------------------------------------------- Imports
import { InternetSeguroSchedule } from "../../types/common";

//-------------------------------------------------------------------------------------------------- Constants
export const DAY_CODES = ["LUN", "MAR", "MIE", "JUE", "VIE", "SAB", "DOM"] as const;
export type DayCode = (typeof DAY_CODES)[number];

export const SCHEDULE_KEY_TO_DAY_CODE: Record<string, DayCode> = {
    mon: "LUN",
    tue: "MAR",
    wed: "MIE",
    thu: "JUE",
    fri: "VIE",
    sat: "SAB",
    sun: "DOM",
};

export const DAY_CODE_TO_SCHEDULE_KEY: Record<DayCode, keyof InternetSeguroSchedule> = {
    LUN: "mon",
    MAR: "tue",
    MIE: "wed",
    JUE: "thu",
    VIE: "fri",
    SAB: "sat",
    DOM: "sun",
};

export const DAY_CODE_TO_DISPLAY_NAME: Record<DayCode, string> = {
    LUN: "Lunes",
    MAR: "Martes",
    MIE: "Miércoles",
    JUE: "Jueves",
    VIE: "Viernes",
    SAB: "Sábado",
    DOM: "Domingo",
};

//-------------------------------------------------------------------------------------------------- Helpers
export const to24Hour = (time12: string): string => {
    const [time, period] = time12.split(" ");
    let [hours, minutes] = time.split(":").map(Number);
    if (period === "am" && hours === 12) {
        hours = 0;
    } else if (period === "pm" && hours !== 12) {
        hours += 12;
    }
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

export const to12Hour = (time24: string): string => {
    const [hours, minutes] = time24.split(":").map(Number);
    const period = hours >= 12 ? "pm" : "am";
    const hour12 = hours % 12 === 0 ? 12 : hours % 12;
    return `${hour12}:${String(minutes).padStart(2, "0")} ${period}`;
};

export const TIME_SLOTS: string[] = (() => {
    const slots: string[] = [];
    for (let h = 0; h < 24; h++) {
        for (let m = 0; m < 60; m += 30) {
            slots.push(to12Hour(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`));
        }
    }
    return slots;
})();

export const hasCompleteRange = (range?: { start: string; end: string }): boolean =>
    !!range && !!range.start && !!range.end;

export const describeSchedule = (schedule: InternetSeguroSchedule): string | null => {
    const dayEntry = Object.entries(schedule).find(
        ([day, value]) => day !== "time_zone" && !!value
    );
    return dayEntry ? String(dayEntry[1]).replace("-", " - ") : null;
};

export interface DayTimeRange {
    start: string;
    end: string;
}

export const scheduleToDayRanges = (
    schedule?: InternetSeguroSchedule
): Record<string, DayTimeRange> => {
    const result: Record<string, DayTimeRange> = {};
    if (!schedule) {
        return result;
    }
    Object.entries(schedule).forEach(([day, value]) => {
        if (day === "time_zone" || !value) {
            return;
        }
        const [start, end] = String(value).split("-");
        if (start && end) {
            const dayCode = SCHEDULE_KEY_TO_DAY_CODE[day];
            if (dayCode) {
                result[dayCode] = { start: to12Hour(start), end: to12Hour(end) };
            }
        }
    });
    return result;
};

export const dayRangesToSchedule = (
    dayRanges: Record<string, DayTimeRange>
): InternetSeguroSchedule => {
    const schedule: InternetSeguroSchedule = {
        time_zone: "America/Mexico_City",
    };
    Object.entries(dayRanges).forEach(([dayCode, range]) => {
        const key = DAY_CODE_TO_SCHEDULE_KEY[dayCode as DayCode];
        if (key && hasCompleteRange(range)) {
            schedule[key] = `${to24Hour(range.start)}-${to24Hour(range.end)}`;
        }
    });
    return schedule;
};